import React, { useState, useMemo, useCallback, useRef, useEffect, useId, useContext, useDeferredValue } from 'react';
import { cn } from '@sglara/cn';
import { breakpoints } from 'design-system/src/tokens/breakpoints';
import { Typography } from 'design-system/src/components/Typography/Typography';
import CancelIcon from 'design-system/src/assets/icons/cancel.svg?react';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import SearchIcon from 'design-system/src/assets/icons/search.svg?react';
import ArrowRightIcon from 'design-system/src/assets/icons/arrowRight.svg?react';
import { createTrackingProps } from 'shared-utils/analytics';
import { Nux } from 'design-system/src/components/Nux/Nux';
import { AuthContext } from '../../AuthContext';
import { useDismissUniversalSearchNux } from '../../hooks/useDismissUniversalSearchNux';

const SEARCH_PLACEHOLDER = 'Search by patient or DOB';
const SEARCH_INPUT_MIN_WIDTH = 420;
const SEARCH_DROPDOWN_ITEM_HEIGHT = 68;

type ClearButtonProps = {
  onClick: (e: React.MouseEvent) => void;
  tableName: string;
};

function ClearButton({ onClick, tableName }: ClearButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'absolute right-3 top-[12px]',
        'h-3 w-3 rounded-full',
        'flex items-center justify-center',
        'cursor-pointer bg-grayscale-600 text-white',
        'p-0 focus-ring',
        'hover:bg-grayscale-700',
      )}
      {...createTrackingProps({ elementName: `${tableName}: Search Clear Button`, unmask: true })}
    >
      <CancelIcon width="7px" height="7px" title="Clear search" />
    </button>
  );
}

type SearchDropdownProps<T> = {
  listRef: React.RefObject<HTMLUListElement>;
  listboxId: string;
  tableName: string;
  isLoading: boolean;
  previewResults: T[];
  highlightedIndex: number;
  isKeyboardNavigation: boolean;
  optionRefs: React.MutableRefObject<(HTMLLIElement | null)[]>;
  getItemKey: (item: T) => string;
  handleItemClick: (item: T) => void;
  handleItemMouseEnter: (index: number) => void;
  handleFooterMouseEnter: () => void;
  renderPreviewItem: (item: T) => React.ReactNode;
  onSeeAllResults: () => void;
  isFooterHighlighted: boolean;
};

function SearchDropdown<T>({
  listRef,
  listboxId,
  tableName,
  isLoading,
  previewResults,
  highlightedIndex,
  isKeyboardNavigation,
  optionRefs,
  getItemKey,
  handleItemClick,
  handleItemMouseEnter,
  handleFooterMouseEnter,
  renderPreviewItem,
  onSeeAllResults,
  isFooterHighlighted,
}: SearchDropdownProps<T>) {
  return (
    <div
      className={cn(
        'absolute left-0 right-0 top-full mt-px',
        'overflow-hidden rounded-medium border border-grayscale-100 bg-white',
        'z-[100] shadow-container',
        'flex flex-col',
      )}
    >
      <ul
        ref={listRef}
        id={listboxId}
        role="listbox"
        aria-label={`${tableName} search suggestions`}
        className={cn('max-h-64 overflow-y-auto', 'm-0 list-none p-0')}
      >
        {isLoading ? (
          <div
            className={cn('box-border flex items-center justify-center px-4')}
            // Tailwind can't parse dynamic class names like h-[${value}px] at build time
            style={{ height: SEARCH_DROPDOWN_ITEM_HEIGHT }}
          >
            <LoadingSpinner light={false} height="16px" size="16px" />
          </div>
        ) : previewResults.length > 0 ? (
          previewResults.map((item, index) => {
            const itemKey = getItemKey(item);
            const isHighlighted = index === highlightedIndex;

            return (
              <React.Fragment key={itemKey}>
                <li
                  ref={(el) => {
                    optionRefs.current[index] = el;
                  }}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={isHighlighted}
                  className={`${cn(
                    'mx-1 box-border cursor-pointer transition duration-200 ease-out',
                    'hover:bg-grayscale-50',
                    isHighlighted && 'bg-grayscale-50',
                  )} ${isHighlighted && isKeyboardNavigation ? 'focus-ring-inset-styles' : ''}`}
                  // Tailwind can't parse dynamic class names like h-[${value}px] at build time
                  style={{ minHeight: SEARCH_DROPDOWN_ITEM_HEIGHT }}
                  onClick={() => handleItemClick(item)}
                  onMouseEnter={() => handleItemMouseEnter(index)}
                  {...createTrackingProps({ elementName: `${tableName}: Search Preview Item` })}
                >
                  <div className="px-3 py-4 transition duration-200 ease-out">{renderPreviewItem(item)}</div>
                </li>
                {index < previewResults.length - 1 && <div className="mx-1 h-px bg-grayscale-200" aria-hidden />}
              </React.Fragment>
            );
          })
        ) : (
          <li
            className={cn('box-border flex items-center justify-center px-4')}
            // Tailwind can't parse dynamic class names like h-[${value}px] at build time
            style={{ height: SEARCH_DROPDOWN_ITEM_HEIGHT }}
            role="option"
            aria-disabled="true"
          >
            <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
              No results found
            </Typography>
          </li>
        )}
      </ul>
      <div className="border-grayscale-100 border-t-solid">
        <button
          type="button"
          onClick={onSeeAllResults}
          onMouseEnter={handleFooterMouseEnter}
          className={`${cn(
            'mx-1 flex w-[calc(100%-8px)] items-center gap-1 bg-white px-3 py-4',
            'border-0 text-purple-700',
            'cursor-pointer transition duration-200 ease-out',
            'focus-ring hover:bg-grayscale-50',
            isFooterHighlighted && 'bg-grayscale-50',
          )} ${isFooterHighlighted && isKeyboardNavigation ? 'focus-ring-inset-styles' : ''}`}
          {...createTrackingProps({ elementName: `${tableName}: See All Results Button`, unmask: true })}
        >
          <span>See all results</span>
          <ArrowRightIcon className="size-4 text-purple-700" aria-hidden />
        </button>
      </div>
    </div>
  );
}

export type TableSearchWithPreviewProps<T> = {
  /** Name of the table for analytics tracking (e.g., 'Prescriptions Table', 'Tasks Table') */
  tableName: string;
  /** Current search value */
  value: string;
  /** Callback when input value changes */
  onChange: (value: string) => void;
  /** Callback when user submits the search (e.g., pressing Enter) */
  onApplySearch: (value: string) => void;
  /** Optional callback when clear button is clicked */
  onClear?: () => void;
  /** Data to search through */
  data: T[];
  /** Function to filter items based on search term. Defaults to showing all items (for server-side filtering). */
  filterFn?: (item: T, searchTerm: string) => boolean;
  /** Function to render each preview item */
  renderPreviewItem: (item: T) => React.ReactNode;
  /** Callback when a preview item is clicked */
  onPreviewItemClick: (item: T) => void;
  /** Function to get a unique key for each item */
  getItemKey: (item: T) => string;
  /** Breakpoint in pixels at which the component switches to full width (default: breakpoints.smallDesktopMax) */
  mobileBreakpoint?: number;
  /** Whether the search data is currently loading */
  isLoading?: boolean;
  /** Minimum number of characters required before showing the dropdown (default: 2) */
  minSearchLength?: number;
  /** Additional class name for the wrapper */
  className?: string;
};

export function TableSearchWithPreview<T>({
  tableName,
  value,
  onChange,
  onApplySearch,
  onClear,
  data,
  filterFn = () => true,
  renderPreviewItem,
  onPreviewItemClick,
  getItemKey,
  mobileBreakpoint = breakpoints.smallDesktopMax,
  isLoading = false,
  minSearchLength = 2,
  className,
}: TableSearchWithPreviewProps<T>) {
  // Local state for the input - updates immediately for responsiveness
  const [localValue, setLocalValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  const { user } = useContext(AuthContext);
  const { mutate: dismissNux } = useDismissUniversalSearchNux();
  const serverShowNux = user?.feature_flags?.show_universal_search_nux ?? false;
  // Local state to immediately hide NUX on dismiss (optimistic update)
  const [localDismissed, setLocalDismissed] = useState(false);
  const showUniversalSearchNux = serverShowNux && !localDismissed;

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Track the last value we sent to parent to distinguish external changes from our own
  const lastSentValueRef = useRef(value);

  // Sync local value only when parent externally clears it (e.g., tab switch)
  // We track what we last sent to distinguish our own debounced updates from external changes
  useEffect(() => {
    // If parent value changed and it wasn't from our debounced update
    if (value !== lastSentValueRef.current) {
      setLocalValue(value);
      lastSentValueRef.current = value;
    }
  }, [value]);

  const id = useId();
  const sanitizedId = id.replace(/:/g, '');
  const componentId = `search-${sanitizedId}`;
  const listboxId = `listbox-${sanitizedId}`;

  // Defer filtering to avoid blocking the UI
  const deferredFilterValue = useDeferredValue(localValue);

  // Filter data by search keyword only for preview
  const previewResults = useMemo(() => {
    if (!deferredFilterValue) return [];
    const searchTerm = deferredFilterValue.toLowerCase();
    return data.filter((item) => filterFn(item, searchTerm));
  }, [data, deferredFilterValue, filterFn]);

  // Reset state when search value changes (user typed something new)
  useEffect(() => {
    setHighlightedIndex(-1);
    setIsKeyboardNavigation(false);
    optionRefs.current = [];
  }, [localValue]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex]);

  const applySearchWithAnnouncement = useCallback(
    (searchValue: string) => {
      onApplySearch(searchValue);
      if (searchValue) {
        setAnnouncement(`Filtering table by: ${searchValue}`);
      } else {
        setAnnouncement('Search cleared');
      }
    },
    [onApplySearch],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const resultsCount = previewResults.length;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (isLoading) break;
          setIsKeyboardNavigation(true);
          if (!isOpen && localValue.length >= minSearchLength) {
            setIsOpen(true);
            setHighlightedIndex(resultsCount > 0 ? 0 : resultsCount); // Go to first item or footer
          } else if (isOpen) {
            setHighlightedIndex((prev) => {
              if (prev === resultsCount) return 0; // Footer → first item
              if (prev === resultsCount - 1) return resultsCount; // Last item → footer
              return prev + 1;
            });
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (isLoading) break;
          setIsKeyboardNavigation(true);
          if (isOpen) {
            setHighlightedIndex((prev) => {
              if (prev === resultsCount) return resultsCount - 1; // Footer → last item
              if (prev === 0 || prev === -1) return resultsCount; // First item → footer
              return prev - 1;
            });
          }
          break;

        case 'Enter':
          // Enter always applies the search to the table
          e.preventDefault();
          // Flush the debounce immediately
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
          lastSentValueRef.current = localValue;
          onChange(localValue);
          applySearchWithAnnouncement(localValue.trim());
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;

        case ' ':
          // Spacebar navigates to highlighted item only if navigated via keyboard
          // (not mouse hover). Otherwise, allow default behavior (typing a space)
          if (isLoading) break;
          if (isOpen && isKeyboardNavigation && highlightedIndex >= 0) {
            if (highlightedIndex === resultsCount) {
              // Footer is highlighted - trigger "See all results"
              e.preventDefault();
              // Flush the debounce immediately
              if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
              }
              lastSentValueRef.current = localValue;
              onChange(localValue);
              applySearchWithAnnouncement(localValue.trim());
              setIsOpen(false);
              setHighlightedIndex(-1);
            } else if (previewResults[highlightedIndex]) {
              e.preventDefault();
              onPreviewItemClick(previewResults[highlightedIndex]);
              setIsOpen(false);
              setHighlightedIndex(-1);
            }
          }
          break;

        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setHighlightedIndex(-1);
          inputRef.current?.focus();
          break;

        case 'Tab':
          // Allow natural tab behavior but close dropdown
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;

        default:
          break;
      }
    },
    [
      localValue,
      onChange,
      applySearchWithAnnouncement,
      isOpen,
      isLoading,
      isKeyboardNavigation,
      highlightedIndex,
      previewResults,
      onPreviewItemClick,
      minSearchLength,
    ],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      // Update local state immediately for responsive input
      setLocalValue(newValue);
      setIsOpen(newValue.length >= minSearchLength);
      setHighlightedIndex(-1);

      // Debounce the parent update to avoid expensive re-renders on every keystroke
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        lastSentValueRef.current = newValue;
        onChange(newValue);
      }, 300);
    },
    [onChange, minSearchLength],
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleItemClick = useCallback(
    (item: T) => {
      onPreviewItemClick(item);
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onPreviewItemClick],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Cancel any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      setLocalValue('');
      lastSentValueRef.current = '';
      onChange('');
      applySearchWithAnnouncement('');
      setIsOpen(false);
      setHighlightedIndex(-1);
      onClear?.();
      inputRef.current?.focus();
    },
    [onChange, applySearchWithAnnouncement, onClear],
  );

  const handleFocus = useCallback(() => {
    if (localValue.length >= minSearchLength) {
      setIsOpen(true);
    }
  }, [localValue, minSearchLength]);

  const handleItemMouseEnter = useCallback((index: number) => {
    setIsKeyboardNavigation(false);
    setHighlightedIndex(index);
  }, []);

  const handleFooterMouseEnter = useCallback(() => {
    setIsKeyboardNavigation(false);
    setHighlightedIndex(previewResults.length);
  }, [previewResults.length]);

  const handleSeeAllResults = useCallback(() => {
    // Flush the debounce immediately
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    lastSentValueRef.current = localValue;
    onChange(localValue);
    applySearchWithAnnouncement(localValue.trim());
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, [onChange, applySearchWithAnnouncement, localValue]);

  // Show options when the dropdown is open and value meets minimum length
  const showOptions = isOpen && localValue.length >= minSearchLength;

  // Responsive styles for the dynamic breakpoint - base styles use Tailwind,
  // this handles the breakpoint-specific mobile overrides
  const responsiveStyles = `
    @media (max-width: ${mobileBreakpoint}px) {
      .${componentId} {
        margin-bottom: 2px;
        margin-left: 0;
        width: 100%;
        min-width: 0;
        border-bottom: none;
      }
    }
  `;

  const handleDismissNux = () => {
    setLocalDismissed(true); // Immediately hide NUX (optimistic update)
    dismissNux(); // Fire off server request
  };

  return (
    <Nux
      showNux={showUniversalSearchNux}
      onDismiss={handleDismissNux}
      title="Search across all statuses"
      description="Search now lives here and works across all your status tabs on this page."
      dismissButtonText="Got it"
    >
      <div
        ref={containerRef}
        // Not using cn() because tailwind-merge filters out custom utilities like border-b-solid
        className={`relative ml-auto border-b-grayscale-100 pb-2 border-b-solid ${componentId} ${className ?? ''}`}
        // Variables don't work in tailwind built-in classes, so we need to use inline style here
        style={{ minWidth: SEARCH_INPUT_MIN_WIDTH }}
      >
        <style>{responsiveStyles}</style>
        {/* Visually hidden live region for screen reader announcements */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
        <div className="relative w-full">
          <SearchIcon
            className="pointer-events-none absolute left-3 top-[11px] size-3.5 text-grayscale-900"
            title={SEARCH_PLACEHOLDER}
          />
          <input
            ref={inputRef}
            type="text"
            className={cn(
              'box-border h-9 w-full rounded-medium border border-solid border-grayscale-100 bg-white',
              localValue ? '!pl-8 !pr-8' : '!px-8',
              'text-grayscale-900 text-body-small-dm-sans',
              'placeholder:text-grayscale-300',
              'transition duration-200 ease-out',
              'focus-ring',
            )}
            aria-label={`${SEARCH_PLACEHOLDER} in the ${tableName}`}
            placeholder={SEARCH_PLACEHOLDER}
            value={localValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            autoComplete="off"
            role="combobox"
            aria-expanded={showOptions}
            aria-haspopup="listbox"
            aria-controls={showOptions ? listboxId : undefined}
            aria-activedescendant={highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined}
            // NOTE: this input cannot be unmasked because it contains PHI
            {...createTrackingProps({ elementName: `${tableName}: Search Input` })}
          />
          {localValue && <ClearButton onClick={handleClear} tableName={tableName} />}
        </div>

        {showOptions && (
          <SearchDropdown
            listRef={listRef}
            listboxId={listboxId}
            tableName={tableName}
            isLoading={isLoading}
            previewResults={previewResults}
            highlightedIndex={highlightedIndex}
            isKeyboardNavigation={isKeyboardNavigation}
            optionRefs={optionRefs}
            getItemKey={getItemKey}
            handleItemClick={handleItemClick}
            handleItemMouseEnter={handleItemMouseEnter}
            handleFooterMouseEnter={handleFooterMouseEnter}
            renderPreviewItem={renderPreviewItem}
            onSeeAllResults={handleSeeAllResults}
            isFooterHighlighted={highlightedIndex === previewResults.length}
          />
        )}
      </div>
    </Nux>
  );
}

export default TableSearchWithPreview;
